import { Message } from '@twilio/conversations';
import { minutesToMilliseconds } from 'date-fns';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { NewMessageToast } from '../../components/NewMessageToast';
import { useAtomCallbackMemoized } from '../../hooks/useAtomCallbackMemoized';
import { currentConversationSidAtom, unreadMessageCountByConversationSidAtom } from '../../session/state';
import { ToastOptions, toast } from '../../toasts';
import { parseIdentity } from '../../video-sdk';
import { twilioIdentityAtom } from '../../video-sdk/twilio';
import { VisitorDrawerState, activeConversationSidAtom, visitorDrawerStateAtom } from '../state';

export const NEW_MESSAGE_TOAST_TAG = 'NEW_MESSAGE_TOAST';
const TOAST_OPTIONS: ToastOptions = {
  purpose: 'realtime',
  timeout: minutesToMilliseconds(1),
  tags: new Set([NEW_MESSAGE_TOAST_TAG]),
};

export function useShowNewMessageToastCallback() {
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);

  const getCurrentConversationSid = useAtomCallbackMemoized((get) => get(currentConversationSidAtom), []);
  const getActiveConversationSid = useAtomCallbackMemoized((get) => get(activeConversationSidAtom), []);
  const setCurrentConversationSid = useSetAtom(currentConversationSidAtom);

  const getTwilioIdentity = useAtomCallbackMemoized((get) => get(twilioIdentityAtom), []);

  const setUnreadMessageCount = useSetAtom(unreadMessageCountByConversationSidAtom);

  return useCallback(
    (message: Message) => {
      const isConversationOpen = !!getCurrentConversationSid();
      const isSelf = message.author === getTwilioIdentity();
      if (isSelf || isConversationOpen) return;
      const senderName = parseIdentity(message.author).name ?? 'New Message';
      const activeConversationSid = getActiveConversationSid();

      // Dismiss any existing toasts first.
      toast.closeByTag(NEW_MESSAGE_TOAST_TAG);

      toast.enqueue(
        ({ closeToast, ...ariaProps }) => (
          <NewMessageToast
            {...ariaProps}
            senderName={senderName}
            messageContent={message.body ?? ''}
            onOpenChatDrawer={() => {
              closeToast();
              setDrawerState(VisitorDrawerState.CHAT);
              setCurrentConversationSid(getActiveConversationSid());
              setUnreadMessageCount((prev) => (activeConversationSid ? { [activeConversationSid]: 0 } : prev));
            }}
          />
        ),
        TOAST_OPTIONS,
      );
    },
    [
      getCurrentConversationSid,
      getActiveConversationSid,
      setDrawerState,
      setCurrentConversationSid,
      setUnreadMessageCount,
      getTwilioIdentity,
    ],
  );
}
